interface Point {
    x: number;
    y: number;
}

interface ShapeOpts {
    sides: number;
    width: number;
    height: number;
    radius: number;
}

interface DrawShapeOpts {
    type?: string;
    sides?: number;
    width?: number;
    height?: number;
    radius?: number;
    size?: number;
    fillColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
    showAngles?: boolean;
    angleOverride?: string;
}

function getVertices(type: string, cx: number, cy: number, opts: ShapeOpts): Point[] {
    const { sides, width, height, radius } = opts;
    const vertices: Point[] = [];

    switch (type) {
        case 'rectangle':
        case 'rectangular':
        case 'square': {
            const w = type === 'square' ? Math.min(width, height) : width;
            const h = type === 'square' ? Math.min(width, height) : height;
            vertices.push({ x: cx - w / 2, y: cy - h / 2 });
            vertices.push({ x: cx + w / 2, y: cy - h / 2 });
            vertices.push({ x: cx + w / 2, y: cy + h / 2 });
            vertices.push({ x: cx - w / 2, y: cy + h / 2 });
            break;
        }
        case 'polygon':
        case 'regular-polygon': {
            const n = Math.max(3, sides);
            for (let i = 0; i < n; i++) {
                const angle = (i * 2 * Math.PI) / n - Math.PI / 2;
                vertices.push({
                    x: cx + radius * Math.cos(angle),
                    y: cy + radius * Math.sin(angle),
                });
            }
            break;
        }
        case 'triangle-right':
            vertices.push({ x: cx - width / 2, y: cy + height / 2 });
            vertices.push({ x: cx + width / 2, y: cy + height / 2 });
            vertices.push({ x: cx - width / 2, y: cy - height / 2 });
            break;
        case 'triangle-equilateral': {
            const h = (Math.sqrt(3) / 2) * width;
            vertices.push({ x: cx, y: cy - h / 2 });
            vertices.push({ x: cx - width / 2, y: cy + h / 2 });
            vertices.push({ x: cx + width / 2, y: cy + h / 2 });
            break;
        }
        case 'triangle-isosceles':
            vertices.push({ x: cx, y: cy - height / 2 });
            vertices.push({ x: cx - width / 2, y: cy + height / 2 });
            vertices.push({ x: cx + width / 2, y: cy + height / 2 });
            break;
        case 'triangle-scalene':
            vertices.push({ x: cx - width / 2, y: cy + height / 2 });
            vertices.push({ x: cx + width / 2, y: cy + height / 4 });
            vertices.push({ x: cx - width / 10, y: cy - height / 2 });
            break;
        case 'trapezium': {
            const topWidth = width * 0.6;
            vertices.push({ x: cx - topWidth / 2, y: cy - height / 2 });
            vertices.push({ x: cx + topWidth / 2, y: cy - height / 2 });
            vertices.push({ x: cx + width / 2, y: cy + height / 2 });
            vertices.push({ x: cx - width / 2, y: cy + height / 2 });
            break;
        }
        case 'parallelogram': {
            const offset = width * 0.2;
            vertices.push({ x: cx - width / 2 + offset, y: cy - height / 2 });
            vertices.push({ x: cx + width / 2 + offset, y: cy - height / 2 });
            vertices.push({ x: cx + width / 2 - offset, y: cy + height / 2 });
            vertices.push({ x: cx - width / 2 - offset, y: cy + height / 2 });
            break;
        }
        case 'rhombus':
            vertices.push({ x: cx, y: cy - height / 2 });
            vertices.push({ x: cx + width / 2, y: cy });
            vertices.push({ x: cx, y: cy + height / 2 });
            vertices.push({ x: cx - width / 2, y: cy });
            break;
        case 'kite':
            vertices.push({ x: cx, y: cy - height / 2 });
            vertices.push({ x: cx + width / 2, y: cy - height / 10 });
            vertices.push({ x: cx, y: cy + height / 2 });
            vertices.push({ x: cx - width / 2, y: cy - height / 10 });
            break;
        case 'star': {
            const n = Math.max(3, sides);
            const innerRadius = radius / 2;
            for (let i = 0; i < n * 2; i++) {
                const r = i % 2 === 0 ? radius : innerRadius;
                const angle = (i * Math.PI) / n - Math.PI / 2;
                vertices.push({
                    x: cx + r * Math.cos(angle),
                    y: cy + r * Math.sin(angle),
                });
            }
            break;
        }
    }
    return vertices;
}

function isPointInPoly(poly: Point[], pt: Point): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        if (
            poly[i].y > pt.y !== poly[j].y > pt.y &&
            pt.x <
                ((poly[j].x - poly[i].x) * (pt.y - poly[i].y)) / (poly[j].y - poly[i].y) + poly[i].x
        ) {
            inside = !inside;
        }
    }
    return inside;
}

function drawAngleMarkers(
    ctx: CanvasRenderingContext2D,
    vertices: Point[],
    override: string,
): void {
    const n = vertices.length;
    if (n < 3) return;

    const overrides = override ? override.split(',').map((s) => s.trim()) : [];

    for (let i = 0; i < n; i++) {
        const pPrev = vertices[(i + n - 1) % n];
        const pCurr = vertices[i];
        const pNext = vertices[(i + 1) % n];

        const v1 = { x: pPrev.x - pCurr.x, y: pPrev.y - pCurr.y };
        const v2 = { x: pNext.x - pCurr.x, y: pNext.y - pCurr.y };

        const a1 = Math.atan2(v1.y, v1.x);
        const a2 = Math.atan2(v2.y, v2.x);

        let diff = a2 - a1;
        while (diff < 0) diff += 2 * Math.PI;
        while (diff > 2 * Math.PI) diff -= 2 * Math.PI;

        const testDist = 5;
        const testBisector = a1 + diff / 2;
        const testPt = {
            x: pCurr.x + testDist * Math.cos(testBisector),
            y: pCurr.y + testDist * Math.sin(testBisector),
        };

        let finalAngle: number;
        let finalBisector: number;
        let startAngle: number;

        if (isPointInPoly(vertices, testPt)) {
            finalAngle = diff;
            finalBisector = a1 + diff / 2;
            startAngle = a1;
        } else {
            finalAngle = 2 * Math.PI - diff;
            finalBisector = a2 + finalAngle / 2;
            startAngle = a2;
        }

        const deg = Math.round((finalAngle * 180) / Math.PI);
        const label = overrides[i] !== undefined && overrides[i] !== '' ? overrides[i] : `${deg}°`;

        const labelDist = 30;
        const lx = pCurr.x + labelDist * Math.cos(finalBisector);
        const ly = pCurr.y + labelDist * Math.sin(finalBisector);

        ctx.save();
        ctx.fillStyle = '#1a1a18';
        ctx.font = '500 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, lx, ly);

        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.lineWidth = 1.5;
        const arcRadius = 15;

        if (deg === 90) {
            const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
            const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
            const u1 = { x: v1.x / len1, y: v1.y / len1 };
            const u2 = { x: v2.x / len2, y: v2.y / len2 };

            ctx.moveTo(pCurr.x + arcRadius * u1.x, pCurr.y + arcRadius * u1.y);
            ctx.lineTo(
                pCurr.x + arcRadius * u1.x + arcRadius * u2.x,
                pCurr.y + arcRadius * u1.y + arcRadius * u2.y,
            );
            ctx.lineTo(pCurr.x + arcRadius * u2.x, pCurr.y + arcRadius * u2.y);
        } else {
            ctx.arc(pCurr.x, pCurr.y, arcRadius, startAngle, startAngle + finalAngle);
        }
        ctx.stroke();
        ctx.restore();
    }
}

export function drawShape(canvas: HTMLCanvasElement, opts?: DrawShapeOpts): void {
    if (!(canvas instanceof HTMLCanvasElement)) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const {
        type = 'circle',
        sides = 3,
        width = 200,
        height = 100,
        radius = 100,
        size = 400,
        fillColor = '#f4a261',
        strokeColor = '#000000',
        strokeWidth = 2,
        showAngles = false,
        angleOverride = '',
    } = opts ?? {};

    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, size, size);

    const cx = size / 2;
    const cy = size / 2;

    const vertices = getVertices(type, cx, cy, { sides, width, height, radius });

    ctx.fillStyle = fillColor;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;

    if (vertices.length > 0) {
        ctx.beginPath();
        ctx.moveTo(vertices[0].x, vertices[0].y);
        for (let i = 1; i < vertices.length; i++) {
            ctx.lineTo(vertices[i].x, vertices[i].y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    } else {
        ctx.beginPath();
        if (type === 'circle') {
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        } else if (type === 'ellipse') {
            ctx.ellipse(cx, cy, width / 2, height / 2, 0, 0, Math.PI * 2);
        } else {
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        }
        ctx.fill();
        ctx.stroke();
    }

    if (showAngles && vertices.length > 0) {
        drawAngleMarkers(ctx, vertices, angleOverride);
    }
}

export const shapes = {
    key: 'shapes',
    name: 'Shape Visualiser',
    draw: drawShape,
    meta: {
        title: 'Shape Visualiser',
        description: 'Generate and customise various mathematical shapes.',
        icon: '⬚',
        href: '/methods/shapes',
    },
};

export default drawShape;
